import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'light' | 'dark';

/**
 * Rotas públicas onde o dark mode NUNCA deve ser aplicado.
 * O login, signup e recuperação de senha devem sempre aparecer no tema claro.
 */
const ROTAS_PUBLICAS_SEM_DARK: string[] = ['/login', '/signup', '/newPasswordRequire'];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'avati-theme';
  private themeSubject = new BehaviorSubject<Theme>('light');
  theme$ = this.themeSubject.asObservable();

  /** Indica se a rota atual é pública (sem dark mode). */
  private emRotaPublica = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  /** Inicializa o tema baseado na preferencia salva ou do sistema */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const saved = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    if (saved === 'dark' || saved === 'light') {
      this.applyTheme(saved);
    } else {
      // Primeiro acesso: inicia sempre no modo claro
      this.applyTheme('light');
    }
  }

  /**
   * Informa ao ThemeService se a rota atual é pública.
   * Em rotas públicas (login, signup, forgot-password), o dark mode é
   * removido do body para garantir que essas telas SEMPRE apareçam no tema claro.
   * O tema salvo não é alterado — ao voltar para o ERP, o dark mode é restaurado.
   */
  aplicarContextoRota(url: string): void {
    const ehPublica = ROTAS_PUBLICAS_SEM_DARK.some(rota => url.startsWith(rota));
    this.emRotaPublica = ehPublica;

    if (!isPlatformBrowser(this.platformId)) return;

    const body = this.doc.body;
    if (ehPublica) {
      // Remove dark-theme do body sem alterar o valor salvo no storage
      body.classList.remove('dark-theme');
      body.classList.add('light-theme');
      this.removeDarkThemeCSS();
    } else {
      // Restaura o tema salvo ao entrar no ERP
      const saved = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
      const temaAtual = saved === 'dark' ? 'dark' : 'light';
      this.applyTheme(temaAtual);
    }
  }

  /** Alterna entre light e dark */
  toggle(): void {
    const next: Theme = this.themeSubject.value === 'light' ? 'dark' : 'light';
    this.applyTheme(next);
  }

  getCurrent(): Theme {
    return this.themeSubject.value;
  }

  isDark(): boolean {
    return this.themeSubject.value === 'dark';
  }

  private applyTheme(theme: Theme): void {
    this.themeSubject.next(theme);
    if (!isPlatformBrowser(this.platformId)) return;

    localStorage.setItem(this.STORAGE_KEY, theme);

    // Em rota pública, não aplicar dark-theme no body mesmo que o valor seja dark
    if (this.emRotaPublica) return;

    const body = this.doc.body;
    if (theme === 'dark') {
      body.classList.add('dark-theme');
      body.classList.remove('light-theme');
      this.loadDarkThemeCSS();
    } else {
      body.classList.remove('dark-theme');
      body.classList.add('light-theme');
      this.removeDarkThemeCSS();
    }
  }

  /** Carrega o CSS do tema dark do PrimeNG dinamicamente */
  private loadDarkThemeCSS(): void {
    if (this.doc.getElementById('primeng-dark-theme')) return;
    const link = this.doc.createElement('link');
    link.id = 'primeng-dark-theme';
    link.rel = 'stylesheet';
    link.href = 'themes/dark/theme.css';
    this.doc.head.appendChild(link);
  }

  /** Remove o CSS do tema dark do PrimeNG */
  private removeDarkThemeCSS(): void {
    const link = this.doc.getElementById('primeng-dark-theme');
    if (link) link.remove();
  }
}
